import React from 'react';
import {
  FlexiblePageTreeNodeType,
  ComponentTreeNode,
  isTabGroupTreeNode,
  isTabListTreeNode,
  isTabPanelsTreeNode,
  isButtonTreeNode,
  isStructuredButtonTreeNode,
  isDisclosureContentTreeNode,
} from '@mutiny-pkg/champaign-schemas/src/schemas/flexiblePages/flexiblePagesTreeNode';
import { Button } from './Button';
import { Container } from './Container';
import { Icon } from './Icon';
import { Image } from './Image';
import { Svg } from './Svg';
import { Text } from './Text';
import { Video } from './Video';
import { Embed } from './Embed';
import { Dialog } from './Dialog';
import { TabGroup } from './TabGroup';
import { TabList } from './TabList';
import { TabPanels } from './TabPanels';
import { Disclosure, DisclosureContent } from './Disclosure';
import { stripLinksFromTreeNode } from '../utils/stripLinksFromTreeNode';
import { CustomComponent, splitCustomComponentData } from '../../customComponents/CustomComponent';

type MentionItems = React.ComponentProps<typeof Text>['mentionItems'];
type RefetchMentionItems = React.ComponentProps<typeof Text>['refetchMentionItems'];

/** Shared context for the read-only render switch — the values it closes over that don't change per node. */
export interface ReadonlyRenderContext {
  overrideVariables?: Record<string, string>;
  /** Already resolved (only set in Preview renderStage). */
  staticAssetsBaseURL?: string | null;
  /** When true, transforms viewport breakpoint classes (lg:) to container query classes (@lg:). */
  isEditor: boolean;
  mentionItems: MentionItems;
  refetchMentionItems: RefetchMentionItems;
}

/**
 * Renders a single component tree node read-only (no DnD wrappers, no interactivity). Recurses over
 * children. Extracted from ReadonlyComponentRenderer so the slide rail can render per-slide thumbnails
 * through the exact same code path without duplicating the switch.
 */
export function renderReadonlyComponentNode(treeNode: ComponentTreeNode, ctx: ReadonlyRenderContext): React.ReactNode {
  const { overrideVariables, staticAssetsBaseURL, isEditor, mentionItems, refetchMentionItems } = ctx;
  switch (treeNode.data.type) {
    case FlexiblePageTreeNodeType.Container: {
      return (
        <Container
          key={treeNode.id}
          id={treeNode.id}
          {...treeNode.data}
          overrideVariables={overrideVariables}
          staticAssetsBaseURL={staticAssetsBaseURL}
          isEditor={isEditor}
        >
          {'children' in treeNode &&
            treeNode.children?.map((childNode: ComponentTreeNode) => renderReadonlyComponentNode(childNode, ctx))}
        </Container>
      );
    }

    case FlexiblePageTreeNodeType.Text: {
      return (
        <Text
          key={treeNode.id}
          id={treeNode.id}
          {...treeNode.data}
          overrideVariables={overrideVariables}
          mentionItems={mentionItems}
          refetchMentionItems={refetchMentionItems}
          isEditor={false}
        />
      );
    }

    case FlexiblePageTreeNodeType.Button: {
      const buttonHasChildren = 'children' in treeNode && treeNode.children && treeNode.children.length > 0;
      return (
        <Button
          key={treeNode.id}
          id={treeNode.id}
          {...treeNode.data}
          overrideVariables={overrideVariables}
          mentionItems={mentionItems}
          refetchMentionitems={refetchMentionItems}
          isEditor={false}
        >
          {buttonHasChildren &&
            treeNode.children?.map((childNode: ComponentTreeNode) =>
              renderReadonlyComponentNode(stripLinksFromTreeNode(childNode), ctx),
            )}
        </Button>
      );
    }

    case FlexiblePageTreeNodeType.StructuredButton: {
      return (
        <Button
          key={treeNode.id}
          id={treeNode.id}
          {...treeNode.data}
          overrideVariables={overrideVariables}
          mentionItems={mentionItems}
          refetchMentionitems={refetchMentionItems}
          isEditor={false}
        >
          {'children' in treeNode &&
            treeNode.children?.map((childNode: ComponentTreeNode) =>
              renderReadonlyComponentNode(stripLinksFromTreeNode(childNode), ctx),
            )}
        </Button>
      );
    }

    case FlexiblePageTreeNodeType.Image: {
      return (
        <Image
          key={treeNode.id}
          id={treeNode.id}
          {...treeNode.data}
          overrideVariables={overrideVariables}
          staticAssetsBaseURL={staticAssetsBaseURL}
        />
      );
    }

    case FlexiblePageTreeNodeType.Video: {
      return <Video key={treeNode.id} id={treeNode.id} {...treeNode.data} />;
    }

    case FlexiblePageTreeNodeType.Svg: {
      return <Svg key={treeNode.id} id={treeNode.id} {...treeNode.data} />;
    }

    case FlexiblePageTreeNodeType.Icon: {
      return <Icon key={treeNode.id} id={treeNode.id} {...treeNode.data} overrideVariables={overrideVariables} />;
    }

    case FlexiblePageTreeNodeType.Embed: {
      return (
        <Embed
          key={treeNode.id}
          id={treeNode.id}
          {...treeNode.data}
          overrideVariables={overrideVariables}
          isEditor={false}
        />
      );
    }

    case FlexiblePageTreeNodeType.Dialog: {
      return (
        <Dialog key={treeNode.id} id={treeNode.id} {...treeNode.data}>
          {'children' in treeNode &&
            treeNode.children?.map((childNode: ComponentTreeNode) => renderReadonlyComponentNode(childNode, ctx))}
        </Dialog>
      );
    }

    case FlexiblePageTreeNodeType.TabGroup: {
      if (!isTabGroupTreeNode(treeNode) || !treeNode.children || treeNode.children.length !== 2) {
        return null;
      }
      return (
        <TabGroup
          {...treeNode.data}
          key={treeNode.id}
          id={treeNode.id}
          renderComponentNode={(node) => renderReadonlyComponentNode(node, ctx)}
          tabGroupNodes={treeNode.children ?? []}
        />
      );
    }

    case FlexiblePageTreeNodeType.TabList: {
      if (!isTabListTreeNode(treeNode)) {
        console.error('Expected TabListTreeNode');
        return null;
      }
      return (
        <TabList key={treeNode.id} id={treeNode.id} {...treeNode.data} data-no-dnd>
          {treeNode.children?.map((tab) => renderReadonlyComponentNode(tab, ctx))}
        </TabList>
      );
    }

    case FlexiblePageTreeNodeType.TabPanels: {
      if (!isTabPanelsTreeNode(treeNode)) {
        console.error('Expected TabPanelsTreeNode');
        return null;
      }
      return (
        <TabPanels key={treeNode.id} id={treeNode.id} {...treeNode.data} data-no-dnd>
          {treeNode.children?.map((panel) => renderReadonlyComponentNode(panel, ctx))}
        </TabPanels>
      );
    }

    case FlexiblePageTreeNodeType.Disclosure: {
      if (!treeNode.children || treeNode.children.length < 2) {
        console.error('Invalid disclosure node structure');
        return null;
      }

      const [buttonNode, disclosureContentNode] = treeNode.children;

      if (!buttonNode || !disclosureContentNode) {
        console.error('Invalid disclosure node structure');
        return null;
      }

      if (!isDisclosureContentTreeNode(disclosureContentNode)) {
        console.error('Second child must be a DisclosureContent node');
        return null;
      }

      if (!isButtonTreeNode(buttonNode) && !isStructuredButtonTreeNode(buttonNode)) {
        console.error('Disclosure trigger must be a StructuredButton node');
        return null;
      }

      let triggerElement: React.ReactNode = null;
      if (isButtonTreeNode(buttonNode)) {
        const buttonTriggerProps = {
          ...buttonNode.data,
          commandfor: disclosureContentNode.data.domTargetId,
          command: '--toggle' as const,
          className: buttonNode.data.className ? `${buttonNode.data.className} group` : 'group',
        };
        triggerElement = (
          <Button
            key={buttonNode.id}
            id={buttonNode.id}
            {...buttonTriggerProps}
            isEditor={false}
            mentionItems={mentionItems}
            refetchMentionitems={refetchMentionItems}
          >
            {'children' in buttonNode &&
              buttonNode.children?.map((childNode: ComponentTreeNode) =>
                renderReadonlyComponentNode(stripLinksFromTreeNode(childNode), ctx),
              )}
          </Button>
        );
      } else {
        let triggerClassName = 'group';
        if (buttonNode.data.className) {
          triggerClassName = `${buttonNode.data.className} group`;
        }
        triggerElement = (
          <Button
            key={buttonNode.id}
            id={buttonNode.id}
            {...buttonNode.data}
            commandfor={disclosureContentNode.data.domTargetId}
            command="--toggle"
            className={triggerClassName}
            isEditor={false}
            mentionItems={mentionItems}
            refetchMentionitems={refetchMentionItems}
          >
            {'children' in buttonNode &&
              buttonNode.children?.map((childNode: ComponentTreeNode) =>
                renderReadonlyComponentNode(stripLinksFromTreeNode(childNode), ctx),
              )}
          </Button>
        );
      }

      return (
        <Disclosure key={treeNode.id} {...treeNode.data} id={treeNode.id}>
          {triggerElement}
          {renderReadonlyComponentNode(disclosureContentNode, ctx)}
        </Disclosure>
      );
    }

    case FlexiblePageTreeNodeType.DisclosureContent: {
      return (
        <DisclosureContent {...treeNode.data} key={treeNode.id} id={treeNode.id}>
          {'children' in treeNode && treeNode.children?.map((childNode) => renderReadonlyComponentNode(childNode, ctx))}
        </DisclosureContent>
      );
    }

    case FlexiblePageTreeNodeType.CustomComponent: {
      const split = splitCustomComponentData(treeNode.data as Record<string, unknown>);
      return (
        <CustomComponent
          key={treeNode.id}
          customComponentId={split.customComponentId}
          customComponentSourceHash={split.customComponentSourceHash}
          className={split.className}
          authorProps={split.authorProps}
        />
      );
    }

    default: {
      console.warn(`Unknown component type: ${treeNode.data.type}`);
      return null;
    }
  }
}
